import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/mdh/Nav";
import { API } from "@/lib/api";


export default function Onboarding() {
    const [params] = useSearchParams();
    const template = params.get("template");
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [result, setResult] = useState(null);
    const [copied, setCopied] = useState(false);
    const [busy, setBusy] = useState(false);

    const create = async () => {
        if (!name.trim() || busy) return;
        setBusy(true);
        try {
            const r = await fetch(`${API}/apps`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: name.trim(),
                    description: description.trim(),
                    template: template || null,
                }),
            });
            if (!r.ok) throw new Error("failed");
            setResult(await r.json());
        } catch {
            toast.error("Could not create the app", { description: "Please try again." });
        } finally {
            setBusy(false);
        }
    };

    const copyKey = async () => {
        try {
            await navigator.clipboard.writeText(result.key);
            setCopied(true);
            toast.success("API key copied");
            setTimeout(() => setCopied(false), 1600);
        } catch {
            toast.error("Copy failed — select it manually");
        }
    };

    return (
        <div data-testid="onboarding-page" className="relative min-h-screen">
            <div className="bg-grid-slate pointer-events-none absolute inset-0 opacity-40" aria-hidden="true" />
            <div className="glow-hero pointer-events-none absolute inset-0" aria-hidden="true" />
            <header className="relative mx-auto flex h-16 max-w-3xl items-center justify-between px-5">
                <Logo />
                <Link
                    to="/dashboard"
                    data-testid="onboarding-skip-link"
                    className="flex items-center gap-1.5 font-mono text-[11px] text-slate-500 hover:text-cyan-300"
                >
                    <ArrowLeft className="h-3 w-3" /> Dashboard
                </Link>
            </header>

            <div className="relative mx-auto max-w-3xl px-5 pb-24 pt-14">
                {!result && (
                    <div>
                        <p className="font-mono text-xs uppercase tracking-[0.3em] text-teal-400/80">
                            New app
                        </p>
                        <h1 className="font-hero mt-4 text-3xl font-bold tracking-tight text-slate-100 sm:text-4xl">
                            Name your app
                        </h1>
                        <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-400">
                            {template
                                ? "One click and it's live — you'll configure the details (URLs, intervals, secrets) from its dashboard panel."
                                : "Create it now, configure it from its dashboard — routes, monitors, destinations and keys all live there."}
                        </p>
                        {template && (
                            <div className="mt-5">
                                <span className="rounded-full border border-cyan-500/30 bg-cyan-950/40 px-3 py-1 font-mono text-[10px] text-cyan-300">
                                    template: {template}
                                </span>
                            </div>
                        )}
                        <input
                            data-testid="onboarding-name-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && create()}
                            placeholder="e.g. Billing webhooks"
                            className="mt-8 w-full max-w-md rounded-md border border-slate-800 bg-slate-950/70 px-4 py-3 font-mono text-sm text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/60 focus:outline-none focus:ring-1 focus:ring-cyan-500/40"
                        />
                        <textarea
                            data-testid="onboarding-description-input"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={2}
                            placeholder="What is it for? (optional)"
                            className="mt-3 w-full max-w-md rounded-md border border-slate-800 bg-slate-950/70 px-4 py-3 font-mono text-xs leading-relaxed text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/60 focus:outline-none"
                        />
                        <div className="mt-6">
                            <button
                                data-testid="onboarding-create-button"
                                onClick={create}
                                disabled={busy || !name.trim()}
                                className="rounded-md bg-cyan-400 px-6 py-3 text-sm font-bold text-slate-950 shadow-[0_0_24px_rgba(6,182,212,0.4)] transition-colors hover:bg-cyan-300 disabled:opacity-50"
                            >
                                {busy ? "Creating…" : "Create app"}
                            </button>
                        </div>
                    </div>
                )}

                {result && (
                    <div>
                        <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 items-center justify-center rounded-full border border-emerald-500/40 bg-emerald-950/50">
                                <Check className="h-5 w-5 text-emerald-400" />
                            </span>
                            <h2
                                data-testid="onboarding-success-title"
                                className="font-display text-xl font-bold text-slate-100"
                            >
                                {result.app.name} is live
                            </h2>
                        </div>
                        <p className="mt-3 max-w-lg text-sm leading-relaxed text-slate-400">
                            Endpoints are registered, delivery is guaranteed, and your dashboard is
                            already streaming. This is your API key — shown once:
                        </p>
                        <div className="glass-panel mt-5 flex items-center justify-between gap-3 rounded-xl px-5 py-4">
                            <code
                                data-testid="onboarding-api-key"
                                className="truncate font-mono text-xs text-cyan-300"
                            >
                                {result.key}
                            </code>
                            <button
                                data-testid="onboarding-copy-key-button"
                                onClick={copyKey}
                                className="flex shrink-0 items-center gap-1.5 rounded border border-slate-700 px-2.5 py-1.5 font-mono text-[10px] text-slate-400 transition-colors hover:border-cyan-500/40 hover:text-cyan-300"
                            >
                                {copied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                                {copied ? "copied" : "copy"}
                            </button>
                        </div>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link
                                to={`/dashboard?app=${result.app.app_id}`}
                                data-testid="onboarding-open-dashboard"
                                className="rounded-md bg-cyan-400 px-6 py-3 text-sm font-bold text-slate-950 shadow-[0_0_24px_rgba(6,182,212,0.4)] transition-colors hover:bg-cyan-300"
                            >
                                Open dashboard
                            </Link>
                            <Link
                                to="/docs"
                                data-testid="onboarding-view-docs"
                                className="rounded-md border border-slate-700 px-5 py-3 text-sm font-semibold text-slate-300 transition-colors hover:border-cyan-500/50 hover:text-cyan-300"
                            >
                                Read the docs
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
